import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { SPARTACUS_UK_TIMEZONE } from 'src/constants/spartacus'

dayjs.extend(utc)
dayjs.extend(timezone)

/** Phase 2 leak sequence — same order as Express `definition.stages`. */
export const CROWS_NEST_FUNNEL_STAGES = Object.freeze([
  {
    key: 'views',
    label: 'Views',
    description: 'Recommendation shown',
  },
  {
    key: 'clicks',
    label: 'Clicks',
    description: 'Recommendation clicked',
  },
  {
    key: 'opened',
    label: 'Opened',
    description: 'Reached the feature screen',
  },
  {
    key: 'firstSuccess',
    label: 'First success',
    description: 'First real save — not a click',
  },
  {
    key: 'repeatUse',
    label: 'Repeat use',
    description: 'Came back after the first win',
  },
  {
    key: 'paywalls',
    label: 'Paywalls',
    description: 'Free limit reached',
    /** North-star only when first success > 0 — UI gates the 🎉. */
    celebrate: true,
  },
])

const LEAK_HINT_SORT_RANK = Object.freeze({
  'Hitting paywall': 0,
  'Paywalls without first success': 1,
  'Opened but never won': 2,
  'One-and-done': 3,
  'Clicks not converting': 4,
  'Low click-through': 5,
  'Active but no paywall yet': 6,
})

export function formatFunnelRate(rate) {
  if (rate == null || Number.isNaN(Number(rate))) return '—'
  return `${Math.round(Number(rate) * 100)}%`
}

export function formatFunnelCount(value) {
  return Number(value) || 0
}

/**
 * Scope line under the funnel title, e.g. "14 Jun – 14 Jul 2026 · Free".
 * Falls back to "Last 30 days · Free" when the API omits range.
 */
export function formatFunnelScopeLabel(range, plan = 'Free') {
  const planLabel =
    plan && String(plan).trim() ? String(plan).trim() : 'All plans'
  if (!range?.fromDate || !range?.toDate) {
    return `Last 30 days · ${planLabel}`
  }

  const from = dayjs(range.fromDate).tz(SPARTACUS_UK_TIMEZONE)
  const to = dayjs(range.toDate).tz(SPARTACUS_UK_TIMEZONE)
  if (!from.isValid() || !to.isValid()) {
    return `Last 30 days · ${planLabel}`
  }

  const sameYear = from.year() === to.year()
  const fromLabel = from.format(sameYear ? 'D MMM' : 'D MMM YYYY')
  const toLabel = to.format('D MMM YYYY')
  return `${fromLabel} – ${toLabel} · ${planLabel}`
}

/**
 * Default table order: celebrate paywalls first, then other leak hints, then volume.
 */
export function sortFunnelFeaturesByPriority(rows = []) {
  return [...rows].sort((a, b) => {
    const payDiff =
      formatFunnelCount(b.paywalls) - formatFunnelCount(a.paywalls)
    if (payDiff !== 0) return payDiff

    const hintA = LEAK_HINT_SORT_RANK[a.leakHint] ?? 4
    const hintB = LEAK_HINT_SORT_RANK[b.leakHint] ?? 4
    if (hintA !== hintB) return hintA - hintB

    const viewDiff = formatFunnelCount(b.views) - formatFunnelCount(a.views)
    if (viewDiff !== 0) return viewDiff

    return String(a.featureName || '').localeCompare(
      String(b.featureName || ''),
    )
  })
}

/**
 * Highlight leaky features: high views / clicks but few first uses or paywalls.
 * Simple score so sales can spot where the ladder drops off.
 */
export function getFunnelLeakHint(row = {}) {
  const views = formatFunnelCount(row.views)
  const clicks = formatFunnelCount(row.clicks)
  const opened = formatFunnelCount(row.opened)
  const firstUses = formatFunnelCount(row.firstUses)
  const firstSuccess = formatFunnelCount(row.firstSuccess)
  const repeatUse = formatFunnelCount(row.repeatUse)
  const paywalls = formatFunnelCount(row.paywalls)

  if (paywalls > 0 && firstSuccess === 0) {
    return 'Paywalls without first success'
  }
  if (views >= 5 && clicks === 0) {
    return 'Low click-through'
  }
  if (clicks >= 3 && opened === 0 && firstSuccess === 0 && firstUses === 0) {
    return 'Clicks not converting'
  }
  if (opened >= 3 && firstSuccess === 0) {
    return 'Opened but never won'
  }
  if (firstSuccess >= 2 && repeatUse === 0) {
    return 'One-and-done'
  }
  if ((firstSuccess >= 2 || firstUses >= 2) && paywalls === 0 && views >= 5) {
    return 'Active but no paywall yet'
  }
  if (paywalls > 0) {
    return 'Hitting paywall'
  }
  return null
}

/**
 * Paywalls with 0 first success are not a healthy conversion.
 * Sales copy must not celebrate that as a working funnel.
 */
export function getFunnelPaywallTrust(counts = {}) {
  const paywalls = formatFunnelCount(counts.paywalls)
  const firstSuccess = formatFunnelCount(counts.firstSuccess)
  if (paywalls > 0 && firstSuccess <= 0) {
    return {
      trusted: false,
      copy: `${paywalls} paywall${
        paywalls === 1 ? '' : 's'
      } with 0 first success — do not treat as a healthy funnel. These can be admin impersonation or limit hits before anyone saved.`,
    }
  }
  return { trusted: true, copy: null }
}

export function shouldCelebrateFunnelPaywalls(counts = {}) {
  return (
    formatFunnelCount(counts.paywalls) > 0 &&
    formatFunnelCount(counts.firstSuccess) > 0
  )
}

function mapFunnelRates(rates = {}) {
  return {
    clickThrough: rates.clickThrough ?? null,
    firstUseFromClick: rates.firstUseFromClick ?? null,
    paywallFromFirstUse: rates.paywallFromFirstUse ?? null,
    openedFromClick: rates.openedFromClick ?? null,
    firstSuccessFromOpened: rates.firstSuccessFromOpened ?? null,
    repeatFromFirstSuccess: rates.repeatFromFirstSuccess ?? null,
    paywallFromRepeat: rates.paywallFromRepeat ?? null,
    paywallFromFirstSuccess: rates.paywallFromFirstSuccess ?? null,
  }
}

function mapFunnelCounts(counts = {}) {
  return {
    views: formatFunnelCount(counts.views),
    clicks: formatFunnelCount(counts.clicks),
    opened: formatFunnelCount(counts.opened),
    firstUses: formatFunnelCount(counts.firstUses),
    firstSuccess: formatFunnelCount(counts.firstSuccess),
    repeatUse: formatFunnelCount(counts.repeatUse),
    paywalls: formatFunnelCount(counts.paywalls),
  }
}

export function mapFunnelSummary(response = {}) {
  const totals = response.totals || {}
  const rates = totals.rates || {}

  return {
    definition: response.definition || null,
    range: response.range || null,
    totals: {
      ...mapFunnelCounts(totals),
      rates: mapFunnelRates(rates),
      paywallTrust: getFunnelPaywallTrust(totals),
    },
    byFeature: sortFunnelFeaturesByPriority(
      (Array.isArray(response.byFeature) ? response.byFeature : []).map(
        row => ({
          key: row.featureId || 'unattributed',
          featureId: row.featureId ?? null,
          featureName: row.featureName || row.featureId || 'Unattributed',
          ...mapFunnelCounts(row),
          rates: mapFunnelRates(row.rates),
          leakHint: getFunnelLeakHint(row),
          paywallTrust: getFunnelPaywallTrust(row),
        }),
      ),
    ),
  }
}
